import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Fetches all type-level permission grants for the current user in a single
 * query, replacing 50+ individual `bulkCheck` calls per page load.
 *
 * Returns a synchronous `hasTypePermission(action, objectType)` lookup
 * function and an `isLoading` flag.
 *
 * Only active when the `trpc` feature flag is enabled. When disabled, the
 * query is not fired and `isLoading` remains `true`.
 */
export const useTypePermissions = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const { data, isLoading } = useQuery({
    ...trpc.frontend.permission.getTypePermissions.queryOptions(),
    enabled: trpcEnabled,
    gcTime: 30 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
  });

  const permissionSet = useMemo(() => new Set(data ?? []), [data]);

  const hasTypePermission = useCallback(
    (action: string, objectType: string): boolean =>
      permissionSet.has(`${action}:${objectType}`),
    [permissionSet]
  );

  return { hasTypePermission, isLoading: trpcEnabled ? isLoading : true };
};
